import React, {
  memo,
  useState,
  useMemo,
  useEffect,
  useCallback,
  CSSProperties,
  ReactNode,
} from 'react';
import { t } from 'ttag';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { areEqual, FixedSizeList as VList } from 'react-window';
import {
  Card as AntdCard,
  Col,
  Modal,
  Row,
  Input,
  List,
  Spin,
  Typography,
  Tooltip,
  message,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import localforage from 'localforage';

import useBunnyFonts, {
  FONT_STATUS_LOADING,
  FONT_STATUS_LOADED,
  FONT_STATUS_FAILED,
  // @ts-ignore
} from '@eventmobi/rich-text/useBunnyFonts';
// @ts-ignore
import { EXTRA_FONTS } from '@eventmobi/rich-text/fonts';

import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const FONTS_STORAGE_KEY = 'CUSTOM_FONTS_EXP';

const Card = styled(AntdCard)`
  &.ant-card.ant-card {
    box-shadow: none;
  }

  &.ant-card.ant-card-bordered {
    border-color: #d9d9d9;
  }

  & > .ant-card-actions {
    padding: 12px 16px;
    border-color: #d9d9d9;
  }

  & > .ant-card-actions > li {
    text-align: left;
    margin: 0;
  }
`;

export const useFonts = () => {
  const [visible, setVisible] = useState(false);
  const [fonts, setFonts] = useState<string[]>([]);

  useEffect(() => {
    const effect = async () => {
      const storedFonts = await localforage.getItem<string[]>(FONTS_STORAGE_KEY);
      if (storedFonts?.length) {
        setFonts(storedFonts);
      }
    };
    effect();
  }, []);

  const updateFonts = useCallback(async newFonts => {
    setFonts(newFonts);
    setVisible(false);
    message.success(t`Selected fonts are successfully added!`);
    await localforage.setItem(FONTS_STORAGE_KEY, newFonts);
  }, []);

  return { visible, fonts, setVisible, updateFonts };
};

const { Search: AntdSearch } = Input;
const { Text } = Typography;

const Search = styled(AntdSearch)`
  // undo left shift for full width
  & > .ant-input-group > .ant-input-group-addon:last-child {
    left: 0;
  }
  // remove extra border from input end adornment
  & > .ant-input-group > .ant-input-group-addon:last-child > .ant-btn-icon-only {
    border-left: none;
  }
`;

interface FontItemProps {
  $isSelected?: boolean;
}

const FontItem = styled(List.Item)<FontItemProps>`
  padding: 7px;
  background-color: ${props => (props.$isSelected ? '#E6F7FF' : 'auto')};
  cursor: pointer;
`;

const SuccessIcon = styled(FontAwesomeIcon)`
  color: ${props => (props.icon === faExclamationCircle ? '#F44336' : '#52C41A')};
  margin-right: 5px;
`;

const MAX_FONTS = 10;
const SELECTED_TEXT_COLOR = '#1890FF';

interface FontProps {
  font: string;
  onClick: (font: string) => void;
  actions: ReactNode[];
  style: CSSProperties;
}

const Font = memo(({ font, onClick, actions, style }: FontProps) => {
  const fonts = useMemo(() => [font], [font]);
  const { status } = useBunnyFonts(fonts);

  return (
    <FontItem style={{ ...style }} onClick={() => onClick(font)} actions={actions}>
      {status === FONT_STATUS_LOADING && <Spin size="small" />}
      {status === FONT_STATUS_LOADED && <Text style={{ fontFamily: font }}>{font}</Text>}
      {status === FONT_STATUS_FAILED && <Text>{font}</Text>}
    </FontItem>
  );
}, areEqual);

interface ItemProps {
  index: number;
  data: {
    list: string[];
    selected?: string[];
    actions: ReactNode[];
    onClick?: (font: string) => void;
  };
  style: CSSProperties;
}

// eslint-disable-next-line react/no-multi-comp
const Item = memo(({ index, data, style = {} }: ItemProps) => {
  const { list, actions, selected, onClick = () => {} } = data;

  const font = list[index];
  const isSelected = selected?.indexOf(font) >= 0 || false;

  if (isSelected) {
    return (
      <FontItem
        style={{ ...style }}
        $isSelected
        onClick={() => onClick(font)}
        actions={[<FontAwesomeIcon icon={faCheck} style={{ color: SELECTED_TEXT_COLOR }} />]}
      >
        <Text style={{ fontFamily: font, color: SELECTED_TEXT_COLOR }}>{font}</Text>
      </FontItem>
    );
  }

  return <Font font={font} style={style} onClick={onClick} actions={actions} />;
}, areEqual);

interface FooterProps {
  selected: string[];
}

const Footer = ({ selected }: FooterProps) => {
  const status = selected.length > MAX_FONTS;

  return (
    <Tooltip title={t`Max limit: 10 fonts`} placement="right">
      <Text>
        <SuccessIcon icon={status ? faExclamationCircle : faCheckCircle} />
        {t`Total: ${selected.length} fonts`}
      </Text>
    </Tooltip>
  );
};

interface Props {
  fonts: string[];
  onClose: () => void;
  onSave: (selected: string[]) => void;
}

const Fonts = ({ onClose, fonts, onSave }: Props) => {
  const [list, setList] = useState(EXTRA_FONTS);
  const [selected, setSelected] = useState(fonts);

  const onSearch = debounce(val => {
    if (!val) {
      setList(EXTRA_FONTS);
    }
    setList(EXTRA_FONTS.filter(f => f.toLowerCase().includes(val.toLowerCase())));
  });

  const removeFont = font => setSelected(selected.filter(f => f !== font));

  const itemData = useMemo(
    () => ({
      onClick: f => {
        if (selected.indexOf(f) < 0) {
          setSelected([...selected, f]);
        } else {
          removeFont(f);
        }
      },
      selected,
      list,
      actions: [],
    }),
    // TODO Remove the eslint-disable and fix the problem
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, list],
  );

  return (
    <Modal
      bodyStyle={{ padding: '16px' }}
      width={800}
      visible
      destroyOnClose
      onOk={() => {
        onSave(selected);
      }}
      onCancel={onClose}
      title={t`Font Library`}
      okText={t`Add to list`}
      okButtonProps={{ disabled: selected.length > MAX_FONTS }}
    >
      <Row gutter={11}>
        <Col span={16}>
          <Search placeholder={t`Search`} onSearch={onSearch} allowClear />
          <List bordered style={{ marginTop: '8px' }}>
            <VList
              height={334} // Total height for 7 fonts items (48*7) - 2px border top and bottom
              itemCount={list.length}
              itemSize={48}
              width="100%"
              itemData={itemData}
            >
              {Item}
            </VList>
          </List>
        </Col>
        <Col span={8}>
          <Card
            title={t`Selected Fonts`}
            bodyStyle={{ padding: 0, height: 270, overflowY: 'auto' }}
            actions={[<Footer selected={selected} />]}
            headStyle={{ color: '#262626', padding: '0 16px', borderColor: '#d9d9d9' }}
          >
            <List>
              {selected.map((font, index) => (
                <Item
                  index={index}
                  data={{
                    list: selected,
                    actions: [
                      <CloseOutlined
                        style={{ fontSize: '9px' }}
                        onClick={() => removeFont(selected[index])}
                      />,
                    ],
                  }}
                  style={{ backgroundColor: '#FAFAFA' }}
                  key={`${font}-selected`}
                />
              ))}
            </List>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default Fonts;
